/**
 * Ordering for the language pickers.
 *
 * Both lists — the one you transcribe from and the one you translate into —
 * were built by walking `STT_LANGUAGES` / `TRANSLATE_LANGUAGES` in array
 * order and translating the labels on the way past. That array is sorted by
 * the ENGLISH names, so a Russian reader got Африкаанс, Албанский,
 * Амхарский, Арабский, Армянский, Азербайджанский… — nearly right, which is
 * worse than plainly wrong, because it looks alphabetical until you go
 * looking for something.
 *
 * It matters more than it sounds: the picker shows only the first 40 entries
 * before you type, so the wrong order also decides which 40 those are.
 */

/** Sort by what the reader actually sees, in their own alphabet.
 *
 * `Intl.Collator` is the only correct comparison here: it knows that Russian
 * sorts Ё with Е, that Swedish puts Å after Z, and that Turkish separates I
 * from İ — none of which `<` on UTF-16 code units gets right. The leading
 * entry ("Auto-detect", "Original") is a command rather than a language and
 * stays pinned at the top. */
export function sortLanguageOptions<T extends { label: string }>(
  options: T[],
  locale: string,
  pinnedLeading = 1,
): T[] {
  const lead = options.slice(0, pinnedLeading)
  const rest = options.slice(pinnedLeading)
  let collator: Intl.Collator
  try {
    collator = new Intl.Collator(locale, { sensitivity: 'base' })
  } catch {
    // An unknown locale tag must not cost the reader a sorted list
    collator = new Intl.Collator(undefined, { sensitivity: 'base' })
  }
  return [...lead, ...rest.sort((a, b) => collator.compare(a.label, b.label))]
}
